import React, { useState, useEffect } from "react";
import { API_BASE_URL, DEVELOPMENT_API_BASE_URL, PRODUCTION_API_BASE_URL } from "../utils/apiConfig";
import TwoFactorLogin from "./TwoFactorLogin";
import {
  Lock,
  Mail,
  Eye,
  EyeOff,
  ArrowRight,
  Shield,
  Wallet,
  TrendingUp,
  CheckCircle,
  Zap,
  Globe,
  AlertCircle,
  X,
  KeyRound,
} from "lucide-react";
import { Navigate, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
const LoginPage = () => {
  const navigate = useNavigate()
  const [isForgotPassword, setIsForgotPassword] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });
  const [otp, setOTP] = useState("");
    const { t } = useTranslation();
  
  const [userId, setUserId] = useState("");
  const [step, setStep] = useState(1); // 1 = login, 2 = otp, 3 = 2fa
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [role, setRole] = useState("");
  // Get environment from localStorage or default based on URL
  const getInitialEnvironment = () => {
    // Check current URL to determine environment
    const currentHost = window.location.hostname;
    if (currentHost === 'dashboard.3pa-y.com') {
      return 'production';
    } else if (currentHost === 'sandbox.3pa-y.com') {
      return 'staging';
    }
    // Fallback to localStorage or default to staging
    return localStorage.getItem('apiEnvironment') || 'staging';
  };

  const [selectedEnvironment, setSelectedEnvironment] = useState(() => {
    return getInitialEnvironment();
  });

  // Update environment when component mounts or URL changes
  useEffect(() => {
    const currentHost = window.location.hostname;
    let environment = 'staging'; // default

    if (currentHost === 'dashboard.3pa-y.com') {
      environment = 'production';
    } else if (currentHost === 'sandbox.3pa-y.com') {
      environment = 'staging';
    }

    // Always sync environment with current URL
    setSelectedEnvironment(environment);
    localStorage.setItem('apiEnvironment', environment);

    // Defensively purge stale session state if the user landed on the login
    // page with an expired or partially-cleared token. Prevents AuthGuard from
    // seeing a half-valid localStorage and redirecting mid-dashboard-load
    // after the next successful login.
    const expiry = localStorage.getItem("token_expiry");
    const userId = localStorage.getItem("userId");
    const isExpired = expiry && Date.now() > parseInt(expiry, 10);
    const isHalfState = userId && !expiry; // userId present but no expiry
    if (isExpired || isHalfState) {
      const env = localStorage.getItem("apiEnvironment");
      localStorage.clear();
      sessionStorage.clear();
      if (env) localStorage.setItem("apiEnvironment", env);
    }
  }, []);
  const [showForgotPasswordModal, setShowForgotPasswordModal] = useState(false);
  const [showOTPModal, setShowOTPModal] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [forgotPasswordErrors, setForgotPasswordErrors] = useState({});
  const [otpCode, setOtpCode] = useState(['', '', '', '', '', '']);
  const [resendTimer, setResendTimer] = useState(0);
  // Get API URL based on selected environment
  const getApiUrl = () => {
    return selectedEnvironment === "production"
      ? PRODUCTION_API_BASE_URL
      : DEVELOPMENT_API_BASE_URL;
  };

  // Handle environment change
  const handleEnvironmentChange = (environment) => {
    setSelectedEnvironment(environment);
    // Store in localStorage so it persists across components
    localStorage.setItem('apiEnvironment', environment);
    // Clear any existing errors when switching environments
    setError("");
    setSuccess("");
    setFormErrors({});
    setForgotPasswordErrors({});
  };

  // 2FA related states
  const [requires2FA, setRequires2FA] = useState(false);
  const [twoFAType, setTwoFAType] = useState("");
  const [userEmail, setUserEmail] = useState("");

  // Validation functions
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validatePassword = (password) => {
    return password.length >= 6;
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.email) {
      errors.email = "Email is required";
    } else if (!validateEmail(formData.email)) {
      errors.email = "Please enter a valid email address";
    }

    if (!isForgotPassword && !formData.password) {
      errors.password = "Password is required";
    } else if (!isForgotPassword && !validatePassword(formData.password)) {
      errors.password = "Password must be at least 6 characters";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const validateForgotPasswordForm = () => {
    const errors = {};

    if (!formData.email) {
      errors.email = "Email is required";
    } else if (!validateEmail(formData.email)) {
      errors.email = "Please enter a valid email address";
    }

    setForgotPasswordErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Input handlers
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });

    // Clear errors when user starts typing
    if (error) setError("");
    if (formErrors[name]) {
      setFormErrors({
        ...formErrors,
        [name]: ""
      });
    }
    if (forgotPasswordErrors[name]) {
      setForgotPasswordErrors({
        ...forgotPasswordErrors,
        [name]: ""
      });
    }
  };
  const handleOTPChange = (e) => {
    const value = e.target.value;
    // Only allow numeric characters and limit to 6 digits
    if (/^\d*$/.test(value) && value.length <= 6) {
      setOTP(value);
    }
    if (error) setError("");
  };

  // OTP Modal handlers
  const handleOTPInputChange = (index, value) => {
    if (!/^\d*$/.test(value)) return;
    const newOtpCode = [...otpCode];
    newOtpCode[index] = value;
    setOtpCode(newOtpCode);

    // Auto-focus next input
    if (value && index < 5) {
      const nextInput = document.getElementById(`otp-${index + 1}`);
      if (nextInput) nextInput.focus();
    }
  };

  const handleOTPKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otpCode[index] && index > 0) {
      const prevInput = document.getElementById(`otp-${index - 1}`);
      if (prevInput) prevInput.focus();
    }
  };

  const startResendTimer = () => {
    setResendTimer(60);
    const timer = setInterval(() => {
      setResendTimer((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };



  const handleOTPSubmit = async () => {
    const otpString = otpCode.join('');
    if (otpString.length !== 6) {
      setError("Please enter the complete 6-digit code.");
      return;
    }

    setIsLoading(true);
    setError("");

    try {
      const response = await fetch(`${getApiUrl()}/auth/verify-otp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: formData.email,
          otp: otpString
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setSuccess("OTP verified successfully!");
        setShowOTPModal(false);
        // Proceed with login
        window.location.href = "#/New-Dashboard";
      } else {
        setError(data.message || "Invalid OTP. Please try again.");
      }
    } catch (error) {
      setError("Network error. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  // Helper function to store user data in localStorage
  const storeUserData = (user, extraPermissions) => {
    try {

      localStorage.setItem("userId", user._id || user.id);
      localStorage.setItem("userRole", user.role);
      localStorage.setItem("userEmail", user.email);
      localStorage.setItem("userName", user.name || "");

      // Fix 1.16: Store permissions for sub-users (permission-based access control)
      if (user.isSubUser) {
        localStorage.setItem("isSubUser", "true");
        localStorage.setItem("parentUser", user.parentUser || "");
        // Store custom permissions array — used by rbacConfig for sub-user access control
        const perms = extraPermissions || user.permissions || [];
        localStorage.setItem("userPermissions", JSON.stringify(perms));
      } else {
        localStorage.setItem("isSubUser", "false");
        localStorage.removeItem("parentUser");
        localStorage.removeItem("userPermissions");
      }

      // ✅ Add session expiry (8 hours from now)
      const expiryTime = Date.now() + 8 * 60 * 60 * 1000; // 8 hours in ms
      localStorage.setItem("token_expiry", expiryTime);

      // Verify storage
      const storedUserId = localStorage.getItem("userId");
      const storedUserRole = localStorage.getItem("userRole");
      const storedUserEmail = localStorage.getItem("userEmail");
      const storedExpiry = localStorage.getItem("token_expiry");
      const storedIsSubUser = localStorage.getItem("isSubUser");
      if (!storedUserId) {
        console.error("Failed to store userId in localStorage");
        setError("Failed to store user session data");
        return false;
      }

      return true;
    } catch (error) {
      console.error("Failed to store user data in localStorage:", error);
      setError("Failed to store user session data");
      return false;
    }
  };

  // Submit logic
  const handleSubmit = () => {
    // navigate('/New-Dashboard');
    if (isForgotPassword) {
      handleForgotPassword();
    } else if (step === 1) {
      if (validateForm()) {
        handleLoginStep1();
      }
    } else if (step === 2) {
      if (validateForm()) {
        handleLoginStep2();
      }
    }
  };

  // Step 1: Email+Pass
  const handleLoginStep1 = async () => {
    setIsLoading(true);
    setError("");
    setSuccess("");
    try {
      const response = await fetch(`${getApiUrl()}/auth/login`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        credentials: "include",
        body: JSON.stringify({
          email: formData.email,
          password: formData.password,
        }),
      });
      const data = await response.json();

      if (!response.ok) {
        setError(data.message || "Login failed");
        return;
      }

      // If 2FA is required directly after login
      if (data?.data?.requires2FA) {
        setRequires2FA(true);
        setTwoFAType(data.data.type2FA);
        setUserEmail(data.data.email);
        setUserId(data.data.userId);
        setStep(3);
        setSuccess(data.message || "Please complete two-factor authentication");
        return;
      }

      // If OTP is required
      if (data?.data?.requiresOTP) {
        setUserId(data.data.userId);
        if (selectedEnvironment === "staging") {
          setShowOTPModal(true);
          startResendTimer();
        } else {
          setSuccess("OTP sent to your email. Enter OTP to continue.");
          setStep(2);
        }
        return;
      }

      // If login directly (should not happen with OTP flow, but fallback)
      if (data.success && data.data?.user) {
        const user = data.data.user;
        if (!["merchant", "super_merchant", "super_operation", "operation", "sub_merchant", "team_member"].includes(user.role)) {
          setError("Only merchant login is allowed.");
          return;
        }

        // Store user data in localStorage (pass permissions from response)
        const storageSuccess = storeUserData(user, data.data?.permissions);
        if (!storageSuccess) {
          return; // Error already set in storeUserData
        }

        setRole(user.role);
        setSuccess("Login successful! Redirecting...");
        setTimeout(() => {
          window.location.href = "#/New-Dashboard";
        }, 1200);
      } else {
        setError(data.message || "Login failed.");
      }
    } catch (error) {
      setError("Network error. Please try again.");
      console.error("Step 1 Error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // Step 2: OTP verify
  const handleLoginStep2 = async () => {
    const otpString = otpCode.join('');
    if (otpString.length !== 6) {
      setError("Please enter the complete 6-digit code.");
      return;
    }
    setIsLoading(true);
    setError("");
    setSuccess("");
    try {
      const response = await fetch(`${getApiUrl()}/auth/login`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        credentials: "include",
        body: JSON.stringify({
          email: formData.email,
          password: formData.password,
          otp: otpString,
        }),
      });
      const data = await response.json();


      if (!response.ok) {
        setError(data.message || "OTP verification failed.");
        return;
      }

      // Check if 2FA is required after OTP verification
      if (data?.data?.requires2FA) {
        setRequires2FA(true);
        setTwoFAType(data.data.type2FA);
        setUserEmail(formData.email);
        setUserId(data.data.userId || userId);
        setStep(3); // Move to 2FA step
        setError(""); // Clear any existing errors
        setSuccess("Please complete two-factor authentication");
        return;
      }

      // OTP verified and login successful (no 2FA required)
      if (data.success && data.data?.user) {
        const user = data.data.user;
        if (!["merchant", "super_merchant", "super_operation", "operation", "sub_merchant", "team_member"].includes(user.role)) {
          setError("Only merchant login is allowed.");
          return;
        }

        // Store user data in localStorage (pass permissions from response)
        const storageSuccess = storeUserData(user, data.data?.permissions);
        if (!storageSuccess) {
          return; // Error already set in storeUserData
        }

        setSuccess("Login successful! Redirecting...");
        setTimeout(() => {
          window.location.href = "#/New-Dashboard";
        }, 1200);
      } else {
        setError(data.message || "OTP verification failed.");
      }
    } catch (error) {
      setError("Network error. Please try again.");
      console.error("Step 2 Error:", error);
    } finally {
      setIsLoading(false);
    }
  };
  useEffect(() => {
  if (success) {
    const timer = setTimeout(() => {
      setSuccess("");
    }, 1000); // 1 second

    return () => clearTimeout(timer); // Cleanup
  }
}, [success]);

  // Handle 2FA success
  const handle2FASuccess = (data) => {

    // Check if we need to proceed with 2FA
    if (!data.success && data.data?.requires2FA) {
      setRequires2FA(false);
      setTwoFAType(data.data.type2FA);
      setUserEmail(data.data.email);
      setUserId(data.data.userId);
      setStep(3);
      setSuccess(data.message || "Please complete two-factor authentication");
      return;
    }

    // Handle successful authentication after 2FA
    if (data.success && data.data?.user) {
      const user = data.data.user;
      // if (user.role !== "merchant") {
      //   setError("Only merchant login is allowed.");
      //   return;
      // }

      // Store user data in localStorage (pass permissions from response)
      const storageSuccess = storeUserData(user, data.data?.permissions);
      if (!storageSuccess) {
        return; // Error already set in storeUserData
      }

      setSuccess("Login successful! Redirecting...");
      setRequires2FA(false); // Reset 2FA state
      setStep(1); // Reset step


      // Force a clean redirect to dashboard
      setTimeout(() => {
        try {
          window.location.replace("#/New-Dashboard");
          // Fallback reload if needed
          setTimeout(() => {
            if (window.location.hash !== "#/New-Dashboard") {
              window.location.href = "#/New-Dashboard";
              window.location.reload();
            }
          }, 100);
        } catch (error) {
          console.error("Redirect error:", error);
          window.location.href = "#/New-Dashboard";
        }
      }, 1000);
    } else {
      console.error("2FA verification failed:", data); // Debug log
      setError(data.message || "2FA verification failed. Please try again.");
    }
  };

  // Handle back from 2FA
  const handle2FABack = () => {
    setError("Please complete Google Authenticator 2FA Verification")
    setStep(1); // Go back to OTP step
    setRequires2FA(false);
    setTwoFAType("");
  };

  // Handle 2FA error
  const handle2FAError = (errorMsg) => {
    setError(errorMsg);
  };

  // Forgot password as earlier
  const handleForgotPassword = async () => {
    if (!validateForgotPasswordForm()) {
      return;
    }

    setIsLoading(true);
    setError("");
    setForgotPasswordErrors({});
    try {
      const response = await fetch(`${getApiUrl()}/auth/forgot-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: formData.email }),
      });
      const data = await response.json();


      if (response.ok) {
        setSuccess("Password reset link sent to your email!");
        setShowForgotPasswordModal(false);
        // Reset form after successful submission
        setTimeout(() => {
          setSuccess("");
        }, 5000);
      } else {
        setError(data.message || "Failed to send reset link.");
      }
    } catch (error) {
      setError("Network error. Please check your connection.");
      console.error("Forgot Password Error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({ email: "", password: "" });
    setError("");
    setSuccess("");
    setOTP("");
    setUserId("");
    setStep(1);
    setFormErrors({});
    setForgotPasswordErrors({});
  };
  const switchToForgotPassword = () => {
    setShowForgotPasswordModal(true);
  };
  const switchToLogin = () => {
    resetForm();
    setIsForgotPassword(false);
  };

  // Features data as earlier
  const features = [
    {
      icon: <Shield className="w-6 h-6" />,
      title: "Bank-Grade Security",
      description: "End-to-end encryption and multi-layer protection",
    },
    {
      icon: <Zap className="w-6 h-6" />,
      title: "Instant Payments",
      description: "Process crypto payments in seconds, not minutes",
    },
    {
      icon: <Globe className="w-6 h-6" />,
      title: "Global Reach",
      description: "Accept payments from customers worldwide",
    },
  ];

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      {/* Header with Red Background */}
      <div className="bg-[#FFFFFF] w-full h-16 flex items-center justify-between px-6">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center">
            <img src="/LOGOM.png" alt="3Pay Logo" id="im" />

            {/* <KeyRound className="w-5 h-5 text-red-600" /> */}
          </div>
          <span className="text-xl font-bold text-black">3PAY</span>
        </div>
        {/* <div className="text-white text-sm">
          Sign in to Console / Production / Email GTT
        </div> */}
      </div>

      {/* Main Content */}
      <div className="flex-1 flex items-center justify-center p-4 relative">

        <div className="w-full max-w-md">

          {/* Form Container */}
          <div className="bg-white rounded-xl p-8 shadow-sm"
            style={{ border: `1px solid ${selectedEnvironment === "staging" ? "#FF9500" : "#02C768"}` }}>
            {/* Error/Success Messages */}
            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center space-x-2">
                <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
                <span className="text-red-700 text-sm">{error}</span>
                <button
                  onClick={() => setError("")}
                  className="ml-auto text-red-500 hover:text-red-700"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            )}
            {success && (
              <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg flex items-center space-x-2">
                <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                <span className="text-green-700 text-sm">{success}</span>
                <button
                  onClick={() => setSuccess("")}
                  className="ml-auto text-green-500 hover:text-green-700"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            )}

            {/* Title */}
            <h1 className="text-2xl font-semibold text-slate-800 mb-6 ">
               {t("login.title")}
              {/* Sign in to Console */}
            </h1>

            {/* Environment Selector */}
            <div
              className="flex  rounded-lg p-1 mb-6"
              style={{ backgroundColor: "#FAFAFB", borderRadius: "15px" }}
            >
              <button
                onClick={() => {
                  handleEnvironmentChange("production");
                  // Only redirect if not already on production URL
                  // if (window.location.hostname !== 'dashboard.3pa-y.com') {
                  //   window.location.href = "https://dashboard.3pa-y.com";
                  // }
                }}
                className={`flex-1 flex items-center justify-center space-x-2 py-2 px-4 rounded-[12px] text-sm font-medium transition-all ${selectedEnvironment === "production"
                  ? "bg-white text-slate-800 shadow-sm"
                  : "text-slate-600 hover:text-slate-800"
                  }`}
              >
                <div
                  className={`w-2 h-2 rounded-full ${selectedEnvironment === "production" ? "bg-green-500" : "bg-orange-500"
                    }`}
                ></div>
                <span>{t("login.production")}</span>
              </button>
              <button
                onClick={() => {
                  handleEnvironmentChange("staging");
                  // Only redirect if not already on staging URL
                  // if (window.location.hostname !== 'sandbox.3pa-y.com') {
                  //   window.location.href = "https://sandbox.3pa-y.com";
                  // }
                }}
                className={`flex-1 flex items-center justify-center space-x-2 py-2 px-4 rounded-[12px] text-sm font-medium transition-all ${selectedEnvironment === "staging"
                  ? "bg-white text-slate-800 shadow-sm"
                  : "text-slate-600 hover:text-slate-800"
                  }`}
              >
                <div
                  className={`w-2 h-2 rounded-full ${selectedEnvironment === "staging" ? "bg-green-500" : "bg-orange-500"
                    }`}
                ></div>
                <span>{t("login.staging")}</span>
              </button>
            </div>

            {/* Form */}
            <div className="space-y-6">
              {/* Step 1: Email/Password */}
              {step === 1 && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-[#1A1C1E] mb-2">
                     {t("login.email")}
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      className={`w-full px-3 py-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 ${formErrors.email ? 'border-2 border-red-300' : ''
                        }`}
                      style={{
                        backgroundColor: "#F6F7F8",
                        borderRadius: "15px"
                      }}
                      placeholder={t("login.enterEmail")}
                      required
                      autoComplete="email"
                    />
                    {formErrors.email && (
                      <p className="text-red-500 text-sm mt-1">{formErrors.email}</p>
                    )}
                  </div>

                  {!isForgotPassword && (
                    <div>
                      <label className="block text-sm font-medium text-[#1A1C1E] mb-2">
                        {t("login.password")}
                      </label>
                      <div className="relative">
                        <input
                          type={!showPassword ? "text" : "password"}
                          name="password"
                          value={formData.password}
                          onChange={handleInputChange}
                          className={`w-full px-3 py-3 pr-12 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 ${formErrors.password ? 'border-2 border-red-300' : ''
                            }`}
                          style={{
                            backgroundColor: "#F6F7F8",
                            borderRadius: "15px"
                          }}
                          placeholder={t("login.enterPassword")}
                          required
                          autoComplete="current-password"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors duration-200"
                        >
                          {showPassword ? (
                            <EyeOff className="w-5 h-5" />
                          ) : (
                            <Eye className="w-5 h-5" />
                          )}
                        </button>
                      </div>
                      {formErrors.password && (
                        <p className="text-red-500 text-sm mt-1">{formErrors.password}</p>
                      )}
                    </div>
                  )}

                  <div className="flex items-center justify-between">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        className="w-4 h-4 text-blue-600 bg-slate-100 border-slate-300 rounded focus:ring-blue-500 focus:ring-2"
                      />
                      <span className="ml-2 text-sm text-slate-600">
                     {t("login.rememberMe")}
                      </span>
                    </label>
                    <button
                      type="button"
                      onClick={switchToForgotPassword}
                      className="text-sm text-blue-600 hover:text-blue-700 transition-colors duration-200"
                    >
                     {t("login.forgotPassword")}
                    </button>
                  </div>
                </>
              )}



              {/* Step 3: 2FA */}
              {step === 3 && requires2FA && (
                <TwoFactorLogin
                  userEmail={userEmail}
                  userId={userId}
                  twoFAType={twoFAType}
                  onSuccess={handle2FASuccess}
                  onBack={handle2FABack}
                  onError={handle2FAError}
                  setSuccess={setSuccess}
                />
              )}

              {/* Button - Hide during 2FA step */}
              {step !== 3 && (
                <button
                  type="button"
                  onClick={handleSubmit}
                  // onClick={() => navigate("/dashboard")}
                  className="w-full text-white py-3 px-4 rounded-lg font-medium transition-all duration-300 flex items-center justify-center space-x-2 shadow-sm hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{
                    background: "linear-gradient(180deg, #3A5AD1 0%, #31ABFF 100%)",
                    borderRadius: "15px"
                  }}
                  disabled={isLoading}
                // className="w-full bg-gradient-to-b from-blue-600 to-blue-500 hover:from-blue-700 hover:to-blue-600 text-white py-3 px-4 rounded-lg font-medium transition-all duration-300 flex items-center justify-center space-x-2 shadow-sm hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoading ? (
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  ) : (
                    <span>
                      {isForgotPassword
                        ?  t("login.sendResetLink")
                        : step === 2
                          ? t("login.verifyOTP")
                          : t("login.login")}
                    </span>
                  )}
                </button>
              )}

              {/* Signup link removed — public merchant signup is disabled.
                  Contact an administrator to register a new merchant account. */}
            </div>

            {/* Back to Login link for Forgot Password, OTP, or 2FA */}
            {(isForgotPassword ||
              step === 2 ||
              (step === 3 && requires2FA)) && (
                <div className="mt-6 text-center">
                  <button
                    onClick={switchToLogin}
                    className="text-sm text-blue-600 hover:text-blue-700 transition-colors duration-200"
                  >
                   {t("login.backToLogin")}
                  </button>
                </div>
              )}
          </div>
        </div>

        {/* Forget Password Modal */}
        {showForgotPasswordModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl p-6 w-full max-w-md shadow-xl">
              {/* Modal Header */}
              <h2 className="text-xl font-semibold text-slate-800 mb-4">
              {t("login.forgetPassword")}
              </h2>
              <div className="border-t border-slate-200 mb-4"></div>

              {/* Modal Content */}
              <p className="text-sm text-slate-600 mb-6">
                 {t("login.forgetPasswordMessage")}{" "}
                <a
                  href="#"
                  style={{ color: "#348BED" }}
                  className="hover:underline"
                >
                 {t("login.contactUs")}
                </a>

              </p>

              {/* Email Input */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  {t("login.email")}
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  name="email"
                  className={`w-full px-3 py-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 ${forgotPasswordErrors.email ? 'border-2 border-red-300' : ''
                    }`}
                  style={{
                    backgroundColor: "#F6F7F8",
                    borderRadius: "15px"
                  }}
                  placeholder={t("login.emailPlace")}
                  required
                  autoComplete="email"
                />
                {forgotPasswordErrors.email && (
                  <p className="text-red-500 text-sm mt-1">{forgotPasswordErrors.email}</p>
                )}
              </div>

              {/* Divider */}

              {/* Modal Actions */}
              <div className="flex space-x-3 border-t border-slate-200 pt-4">
                <button
                  onClick={() => {
                    setShowForgotPasswordModal(false);
                    setError("");
                    setSuccess("");
                    setForgotPasswordErrors({});
                  }}
                  className="flex-1 px-0 py-3 bg-white border border-slate-300 text-slate-800 rounded-lg hover:bg-slate-50 transition-colors duration-200 font-medium"
                  style={{
                    borderColor: "#D1D5DB",
                    color: "#374151",
                    borderRadius: "15px"
                  }}
                >
                  {t("login.Back")}
                </button>
                <button
                  onClick={handleForgotPassword}
                  disabled={isLoading}
                  className="flex-1 px-4 py-3 text-white rounded-lg transition-colors duration-200 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{
                    background: "linear-gradient(180deg, #3A5AD1 0%, #31ABFF 100%)",
                    borderRadius: "15px"
                  }}
                >
                  {isLoading ? (
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mx-auto"></div>
                  ) : (
                    t("login.sendVerificationCode")
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* OTP Verification Modal Email */}
        {/* {showOTPModal && ( */}
        {(showOTPModal || (step === 2 && !requires2FA)) && (

          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl p-4 sm:p-8 w-full max-w-[340px] sm:max-w-md shadow-xl">
              {/* Modal Header */}
              <div className="flex items-center space-x-3 mb-3">
                {/* <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <Mail className="w-5 h-5 text-blue-600" />
              </div> */}
                <h2 className="text-lg sm:text-xl font-semibold text-slate-800">
                  {t("login.otpVerification")}
                </h2>

              </div>
              <div className="border-t border-slate-200 mb-4"></div>


              {/* Modal Content */}
              <p className="text-xs sm:text-sm text-slate-600 mb-6">
                 {t("login.enterOTP")}
              </p>

              {/* OTP Input Fields */}
              <div className="mb-6 sm:mb-9">
                <div className="flex justify-center space-x-1 sm:space-x-2">
                  {otpCode.map((digit, index) => (
                    <div key={index} className="flex space-x-1">
                      <input
                        id={`otp-${index}`}
                        type="text"
                        maxLength="1"
                        value={digit}
                        onChange={(e) => handleOTPInputChange(index, e.target.value)}
                        onKeyDown={(e) => handleOTPKeyDown(index, e)}
                        className="w-10 h-10 sm:w-12 sm:h-12 text-center text-base sm:text-lg font-semibold border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                        style={{ backgroundColor: "#F6F7F8" }}
                      />
                      {index === 2 && <span className="text-slate-400 text-lg sm:text-xl self-center px-0.5">-</span>}
                    </div>
                  ))}
                </div>
              </div>

              {/* Resend Timer */}


              {/* Error Message */}
              {error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center space-x-2">
                  <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
                  <span className="text-red-700 text-sm">{error}</span>
                  <button
                    onClick={() => setError("")}
                    className="ml-auto text-red-500 hover:text-red-700"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              )}

              {/* Success Message */}
              {success && (
                <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg flex items-center space-x-2">
                  <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                  <span className="text-green-700 text-sm">{success}</span>
                  <button
                    onClick={() => setSuccess("")}
                    className="ml-auto text-green-500 hover:text-green-700"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              )}
              <div className="border-t border-slate-200 mb-4"></div>

              {/* Action Buttons */}
              <div className="flex space-x-3">
                {/* Back to Login Button */}
                <button
                  onClick={() => {
                    setShowOTPModal(false);
                    setStep(1); // Reset to login step
                    setOtpCode(['', '', '', '', '', '']);
                    setError("");
                    setSuccess("");
                    setFormData({ email: "", password: "" }); // Clear form data
                  }}
                  style={{ borderRadius: "15px" }}
                  className="flex-1 text-slate-600 py-3 px-4 rounded-lg font-medium transition-all duration-300 flex items-center justify-center space-x-2 border border-slate-300 hover:bg-slate-50"
                >
                  <span>{t("login.Back")}</span>
                </button>

                {/* Continue Button */}
                <button
                  onClick={handleLoginStep2}
                  disabled={isLoading || otpCode.join('').length !== 6}
                  className="flex-1 text-white py-3 px-4 rounded-lg font-medium transition-all duration-300 flex items-center justify-center space-x-2 shadow-sm hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{
                    background: "linear-gradient(180deg, #3A5AD1 0%, #31ABFF 100%)",
                    borderRadius: "15px"
                  }}
                >
                  {isLoading ? (
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  ) : (
                    t("login.continue")
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default LoginPage;
